import logo from '@/assets/logo.png';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { cn } from '@/lib/utils';
import { useFormik } from 'formik';
import { useState } from 'react';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { InputWithError } from './InputWithError';

export const RegisterForm = ({ className, ...props }: React.ComponentProps<'div'>) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const { executeRecaptcha } = useGoogleReCaptcha();

  const token = searchParams.get('invitation');
  const firstname = searchParams.get('firstname') || '';
  const lastname = searchParams.get('lastname') || '';

  const formik = useFormik({
    initialValues: {
      firstName: firstname,
      lastName: lastname,
      password: '',
    },
    validate: (values) => {
      const errors: {
        firstName?: string;
        lastName?: string;
        password?: string;
      } = {};

      if (!values.firstName.trim()) {
        errors.firstName = 'First name is required';
      }

      if (!values.lastName.trim()) {
        errors.lastName = 'Last name is required';
      }

      if (!values.password) {
        errors.password = 'Password is required';
      } else if (values.password.length < 8) {
        errors.password = 'Password must be at least 8 characters';
      }

      return errors;
    },
    onSubmit: async (values) => {
      if (!token) {
        toast.error('Invalid invitation link', {
          description: 'The invitation link is missing or invalid',
        });
        return;
      }

      setIsLoading(true);

      const toastId = toast.loading('Creating account...', {
        description: 'Please wait while we set up your account',
      });

      try {
        // Get reCAPTCHA token
        const captchaToken = await executeRecaptcha?.('register');

        const response = await fetch(`${import.meta.env.VITE_ENDPOINT}/register`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            password: values.password,
            firstName: values.firstName.trim(),
            lastName: values.lastName.trim(),
            captchaToken,
            tokenId: token,
          }),
        });

        const data = await response.json();

        if (!response.ok) {
          throw new Error(data.error || 'Failed to create account');
        }

        if (data.success) {
          setIsSubmitted(true);
          toast.success('Account created successfully!', {
            description: 'Please login to your account to access your GIS account',
            id: toastId,
          });
          // Redirect to dashboard after a short delay
          navigate('/auth/login', { replace: true });
        } else {
          throw new Error('Invalid response from server');
        }
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Failed to create account';
        toast.error('Failed to create account', {
          description: errorMessage,
          id: toastId,
        });
      } finally {
        setIsLoading(false);
      }
    },
  });

  if (isSubmitted) {
    return (
      <div className={cn('w-full', className)} {...props}>
        <Card>
          <CardContent className='p-6'>
            <div className='flex flex-col items-center space-y-4'>
              <div className='w-16 h-16 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center'>
                <svg className='w-8 h-8 text-green-600 dark:text-green-400' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
                  <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M5 13l4 4L19 7' />
                </svg>
              </div>
              <div className='text-center'>
                <h2 className='text-2xl font-bold'>Account Created Successfully!</h2>
                <p className='text-muted-foreground mt-2'>Welcome to your GIS account. Redirecting to dashboard...</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className={cn('w-full', className)} {...props}>
      <Card className='overflow-hidden p-0'>
        <CardContent className='grid p-0 md:grid-cols-2 bg-white dark:bg-stone-900'>
          <form onSubmit={formik.handleSubmit} className='space-y-4 p-8'>
            <div className='flex flex-col items-center space-y-4 mb-6'>
              <img src={logo} alt='Actegon Solutions' className='h-16 w-auto' />
              <div className='text-center'>
                <h1 className='text-2xl font-bold'>Create Your Account</h1>
                <p className='text-muted-foreground mt-2'>You've been invited to join an organization</p>
              </div>
            </div>
            <InputWithError
              label='First Name'
              id='firstName'
              name='firstName'
              type='text'
              value={formik.values.firstName}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={formik.touched.firstName ? formik.errors.firstName : undefined}
              placeholder='Enter your first name'
              disabled={isLoading}
            />

            <InputWithError
              label='Last Name'
              id='lastName'
              name='lastName'
              type='text'
              value={formik.values.lastName}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={formik.touched.lastName ? formik.errors.lastName : undefined}
              placeholder='Enter your last name'
              disabled={isLoading}
            />
            <InputWithError
              label='Password'
              id='password'
              name='password'
              type='password'
              value={formik.values.password}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={formik.touched.password ? formik.errors.password : undefined}
              placeholder='Enter your password'
              disabled={isLoading}
            />

            <Button type='submit' className='w-full' disabled={isLoading}>
              {isLoading ? 'Creating Account...' : 'Create Account'}
            </Button>
            <div className='mt-6 text-center'>
              <p className='text-sm text-muted-foreground'>
                Already have an account?{' '}
                <Link to='/auth/login' className='font-medium underline underline-offset-4 hover:text-primary'>
                  Sign in
                </Link>
              </p>
            </div>
          </form>
          <div className='bg-muted relative hidden md:block'>
            <img
              src='/placeholder.svg'
              alt='Image'
              className='absolute inset-0 h-full w-full object-cover dark:brightness-[0.2] dark:grayscale'
            />
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
